import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Step,
  Stepper,
  StepLabel,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { WidgetType, DashboardLayout, CreateWidgetRequest, getWidgetTypeId, createDefaultDataSource, DashboardWidget, getWidgetType } from 'types/dashboard.ui';
import { dashboardAPI } from '../../api/dashboard/index';

// Импорт подкомпонентов
import { useWidgetTypes, getDefaultWidgetConfig, findWidgetTypeInfo } from './widget.types.config';
import WidgetTypeSelection from './widget.wizard.step1';
import WidgetSettings from './widget.wizard.step2';
import WidgetConfirmation from './widget.wizard.step3';

interface WidgetCreationWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (widget: any) => void;
  dashboardId: string;
  widget?: DashboardWidget; // Опциональный виджет для редактирования
}

interface FormData {
  selectedType?: WidgetType;
  title: string;
  config: any;
}

const WidgetCreationWizard: React.FC<WidgetCreationWizardProps> = ({
  open,
  onClose,
  onSuccess,
  dashboardId,
  widget, // Виджет для редактирования
}) => {
  const translate = useTranslate();
  const widgetTypes = useWidgetTypes();
  const isEditMode = !!widget; // Определяем режим редактирования
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    config: {},
  });

  // Инициализация формы при открытии диалога
  React.useEffect(() => {
    if (open) {
      if (widget) {
        // Режим редактирования - заполняем данными виджета
        const widgetType = getWidgetType(widget);
        
        // Парсим config и data_source (или используем напрямую, если уже объекты)
        let parsedConfig: any = {};
        let parsedDataSource: any = null;
        
        try {
          if (typeof widget.config === 'string') {
            parsedConfig = widget.config ? JSON.parse(widget.config) : {};
          } else if (typeof widget.config === 'object') {
            parsedConfig = widget.config || {};
          }
        } catch (e) {
          console.error('Error parsing widget config:', e);
        }
        
        try {
          if (typeof widget.data_source === 'string') {
            parsedDataSource = widget.data_source ? JSON.parse(widget.data_source) : null;
          } else if (typeof widget.data_source === 'object') {
            parsedDataSource = widget.data_source || null;
          }
        } catch (e) {
          console.error('Error parsing widget data_source:', e);
        }
        
        // Получаем дефолтную конфигурацию для типа виджета
        const defaultConfig = widgetType ? getDefaultWidgetConfig(widgetType, translate) : {};
        
        // Объединяем: дефолтные значения + сохраненный config + dataSource
        const fullConfig = {
          ...defaultConfig,
          ...parsedConfig,
          dataSource: parsedDataSource || parsedConfig.dataSource || defaultConfig.dataSource,
        };
        
        setFormData({
          selectedType: widgetType,
          title: widget.title,
          config: fullConfig,
        });
        // В режиме редактирования начинаем с шага настроек
        setCurrentStep(1);
      } else {
        // Режим создания - сброс формы
        setFormData({
          title: '',
          config: {},
        });
        setCurrentStep(0);
      }
    }
  }, [open, widget, translate]);

  const steps = [
    translate('resources.dashboards.widget_wizard.step_type'),
    translate('resources.dashboards.widget_wizard.step_settings'),
    translate('resources.dashboards.widget_wizard.step_confirmation')
  ];

  const handleTypeSelect = (type: WidgetType) => {
    const typeInfo = findWidgetTypeInfo(widgetTypes, type);
    setFormData(prev => {
      // Если тип изменился, полностью сбрасываем конфигурацию
      const isTypeChanged = prev.selectedType !== type;
      
      return {
        ...prev,
        selectedType: type,
        // Сбрасываем title только если тип изменился или title не был изменен пользователем
        title: isTypeChanged ? translate('resources.dashboards.widget_wizard.new_widget_title', { type: typeInfo?.name }) : prev.title,
        // Всегда сбрасываем конфигурацию при смене типа
        config: getDefaultWidgetConfig(type, translate),
      };
    });
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.selectedType) return;

    setLoading(true);
    
    try {
      if (isEditMode && widget) {
        // Режим редактирования - обновляем существующий виджет
        const configToSave = JSON.stringify(formData.config);
        
        // Для data_source берем из config.dataSource, а не используем createDefaultDataSource
        let dataSourceToSave = {};
        if (formData.config.dataSource) {
          dataSourceToSave = formData.config.dataSource;
        } else {
          dataSourceToSave = createDefaultDataSource(formData.selectedType);
        }
        
        const updateRequest = {
          id: widget.id,
          title: formData.title,
          position_x: widget.position_x,
          position_y: widget.position_y,
          width: widget.width,
          height: widget.height,
          config: configToSave,
          data_source: JSON.stringify(dataSourceToSave),
          order_index: widget.order_index || 0,
          active: widget.active !== undefined ? widget.active : true,
        };

        const updatedWidget = await dashboardAPI.updateWidget(widget.id, updateRequest);
        
        onSuccess(updatedWidget);
        handleClose();
      } else {
        // Режим создания - создаем новый виджет
        const typeInfo = widgetTypes.find(t => t.type === formData.selectedType);
        const layout: DashboardLayout = {
          i: `widget-${Date.now()}`,
          x: 0,
          y: 0,
          w: typeInfo?.defaultSize.w || 6,
          h: typeInfo?.defaultSize.h || 4,
        };

        // Для data_source берем из config.dataSource
        let dataSourceToSave = {};
        if (formData.config.dataSource) {
          dataSourceToSave = formData.config.dataSource;
        } else {
          dataSourceToSave = createDefaultDataSource(formData.selectedType);
        }

        const request: CreateWidgetRequest = {
          dashboard_id: dashboardId,
          widget_type_id: getWidgetTypeId(formData.selectedType),
          title: formData.title,
          position_x: layout.x,
          position_y: layout.y,
          width: layout.w,
          height: layout.h,
          config: JSON.stringify(formData.config),
          data_source: JSON.stringify(dataSourceToSave),
        };

        const newWidget = await dashboardAPI.createWidget(request);
        onSuccess(newWidget);
        handleClose();
      }
    } catch (error) {
      console.error('Error creating widget:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setFormData({
      title: '',
      config: {},
    });
    onClose();
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return formData.selectedType !== undefined;
      case 1:
        if (formData.title.trim() === '') return false;
        
        // Для Chart проверяем наличие источников данных
        if (formData.selectedType === 'chart') {
          return formData.config.dataSource?.items?.length > 0;
        }
        
        return true;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    const selectedTypeInfo = findWidgetTypeInfo(widgetTypes, formData.selectedType);
    
    switch (currentStep) {
      case 0:
        return (
          <WidgetTypeSelection
            widgetTypes={widgetTypes}
            selectedType={formData.selectedType}
            onTypeSelect={handleTypeSelect}
          />
        );

      case 1:
        return (
          <WidgetSettings
            widgetType={formData.selectedType}
            title={formData.title}
            config={formData.config}
            onTitleChange={(title) => setFormData(prev => ({ ...prev, title }))}
            onConfigChange={(config) => setFormData(prev => ({ ...prev, config }))}
          />
        );

      case 2:
        return (
          <WidgetConfirmation
            widgetTypeInfo={selectedTypeInfo}
            title={formData.title}
            config={formData.config}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditMode 
              ? translate('resources.dashboards.widget_wizard.edit_title')
              : translate('resources.dashboards.widget_wizard.title')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label, index) => {
              // В режиме редактирования пропускаем первый шаг (выбор типа)
              if (isEditMode && index === 0) return null;
              return (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
        <Button
          onClick={handlePrevious}
          disabled={(isEditMode ? currentStep === 1 : currentStep === 0) || loading}
          startIcon={<ArrowBackIcon />}
        >
          {translate('resources.dashboards.widget_wizard.back')}
        </Button>

        <Box>
          {currentStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!isStepValid() || loading}
              variant="contained"
              endIcon={<ArrowForwardIcon />}
            >
              {translate('resources.dashboards.widget_wizard.next')}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!isStepValid() || loading}
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            >
              {loading 
                ? (isEditMode 
                    ? translate('resources.dashboards.widget_wizard.updating')
                    : translate('resources.dashboards.widget_wizard.creating'))
                : (isEditMode
                    ? translate('resources.dashboards.widget_wizard.update')
                    : translate('resources.dashboards.widget_wizard.create'))}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetCreationWizard;
